import { create } from "zustand";

/**
 * Global authentication store using Zustand
 *
 * Manages authenticated user state including:
 * - User profile (name, email)
 * - Subscription/product status (currentProduct, subscribedProducts, isPaid)
 * - Account settings (preferredLanguage, onboarding_completed, isEmailVerified)
 * - Feature access helpers (hasCorporateAccess, hasCourseAccess, isAuthenticated)
 *
 * Note: Handles two API response formats:
 * 1. Login response: data wrapped in "auth" key
 * 2. Flag/status responses: data at root level
 *
 * @returns {Object} Auth store with state and actions
 */
const useAuthStore = create((set, get) => ({
  /**
   * Authenticated user profile information
   * @type {Object}
   * @property {string} name - User's full name
   * @property {string} email - User's email address (used as unique identifier)
   */
  user: {
    name: "",
    email: "",
  },

  /**
   * Whether user has an active paid subscription
   * @type {boolean}
   */
  isPaid: false,

  /**
   * Whether user's email has been verified
   * @type {boolean}
   */
  isEmailVerified: false,

  /**
   * Current product user is subscribed to
   * Values: "corporate", "preventive_course", "physio_therapist", or null
   * @type {string|null}
   */
  currentProduct: null,

  /**
   * Array of all products user is subscribed to
   * Allows users to have multiple active subscriptions
   * @type {Array<string>}
   */
  subscribedProducts: [],

  /**
   * Whether user has completed initial onboarding flow
   * @type {boolean}
   */
  onboarding_completed: false,

  /**
   * User's preferred language for UI
   * Defaults to German ("de"); can be "en" for English
   * @type {string}
   */
  preferredLanguage: "de",

  /**
   * Set complete authentication data from API response
   *
   * Handles two API response formats:
   * 1. Login: {auth: {name, email, isPaid, isEmailVerified, current_product, ...}}
   * 2. Flags: {name, email, isPaid, isEmailVerified, current_product, ...}
   *
   * Automatically detects format and extracts data accordingly.
   *
   * @param {Object} authData - Complete auth data from API
   * @param {Object} [authData.auth] - Optional wrapper for login responses
   * @param {string} authData.name - User's full name
   * @param {string} authData.email - User's email
   * @param {boolean} authData.isPaid - Subscription status
   * @param {boolean} authData.isEmailVerified - Email verification status
   * @param {string} authData.current_product - Current product subscription
   * @param {Array} authData.subscribed_products - Array of subscribed products
   * @param {boolean} authData.onboarding_completed - Onboarding completion status
   * @param {string} authData.preferredLanguage - User's language preference
   */
  setAuthData: (authData) => {
    // Detect response format: login has "auth" wrapper, flags response doesn't
    const hasAuthWrapper = authData.auth !== undefined;

    // Extract user data from appropriate level based on response format
    const newState = {
      user: {
        name: hasAuthWrapper ? authData.auth.name : authData.name,
        email: hasAuthWrapper ? authData.auth.email : authData.email,
      },
      // Extract subscription status
      isPaid: hasAuthWrapper ? authData.auth.isPaid : authData.isPaid,
      // Extract email verification status
      isEmailVerified: hasAuthWrapper
        ? authData.auth.isEmailVerified
        : authData.isEmailVerified,
      // Extract current product (note: API uses snake_case current_product)
      currentProduct: hasAuthWrapper
        ? authData.auth.current_product
        : authData.current_product,
      // Extract array of all subscribed products
      subscribedProducts: authData.subscribed_products || [],
      // Extract onboarding completion status
      onboarding_completed: hasAuthWrapper
        ? authData.auth.onboarding_completed
        : authData.onboarding_completed || false,
      // Extract language preference with German default
      preferredLanguage: hasAuthWrapper
        ? authData.auth.preferredLanguage || "de"
        : authData.preferredLanguage || "de",
    };

    // Update store state
    set(newState);
  },

  /**
   * Update user profile information only
   *
   * Useful for partial updates (e.g., after name change)
   * Handles both wrapped (auth key) and unwrapped API response formats.
   *
   * @param {Object} userData - User data with optional "auth" wrapper
   * @param {string} userData.name - User's full name
   * @param {string} userData.email - User's email
   */
  setUser: (userData) => {
    // Detect response format: login has "auth" wrapper, flags response doesn't
    const hasAuthWrapper = userData.auth !== undefined;

    // Update only user profile, preserve other state
    set({
      user: {
        name: hasAuthWrapper ? userData.auth.name : userData.name,
        email: hasAuthWrapper ? userData.auth.email : userData.email,
      },
    });
  },

  /**
   * Clear all authentication data (logout action)
   *
   * Resets entire auth state to defaults (empty user, all flags false/null)
   */
  clearAuth: () => {
    set({
      user: { name: "", email: "" },
      isPaid: false,
      isEmailVerified: false,
      currentProduct: null,
      subscribedProducts: [],
      onboarding_completed: false,
      preferredLanguage: "de",
    });
  },

  /**
   * Clear user profile only (keep subscription/account flags)
   *
   * Useful for partial logout scenarios
   */
  clearUser: () => {
    set({
      user: { name: "", email: "" },
    });
  },

  /**
   * Update user's language preference and persist to store
   *
   * @param {string} language - Language code ("de" for German, "en" for English)
   */
  setPreferredLanguage: (language) => {
    set({ preferredLanguage: language });
  },

  /**
   * Check if user has corporate product access
   *
   * @returns {boolean} True if currentProduct === "corporate"
   */
  hasCorporateAccess: () => {
    const state = get();
    return state.currentProduct === "corporate";
  },

  /**
   * Check if user has preventive course product access
   *
   * @returns {boolean} True if currentProduct === "preventive_course"
   */
  hasCourseAccess: () => {
    const state = get();
    return state.currentProduct === "preventive_course";
  },

  /**
   * Check if user is currently authenticated
   *
   * A user is authenticated if they have a non-empty email address.
   *
   * @returns {boolean} True if user.email is set
   */
  isAuthenticated: () => {
    const state = get();
    return !!state.user.email;
  },

  /**
   * Get user's current language preference
   *
   * @returns {string} Language code ("de" or "en")
   */
  getLanguage: () => {
    return get().preferredLanguage;
  },
}));
/**
 * Global Zustand auth store for managing user authentication state
 *
 * Usage:
 *   import useAuthStore from './authStore'
 *   const { user, isPaid, setAuthData, isAuthenticated } = useAuthStore()
 */
export default useAuthStore;
